import { createGlobalState } from './createGlobalState';
import { equalPermission, Permission } from 'core/models/scope';
import { useEffect, useMemo } from 'react';
import { useAuthTokenRepository } from './useAuthTokenRepository';
import { cancellable } from './utility/cancellable';
import { Project } from 'core/models/project';
import { Environment } from 'core/models/environment';

function makePermission(
    operations: string[],
    projectId = '',
    environmentId = '',
): Permission {
    return {
        resource: {
            projectId,
            environmentId,
        },
        operations,
    };
}

const _knownPermissions = {
    getVehiclesScope: makePermission([
        'DescribeVehicleMetadata',
        'DescribeVehicleTelemetry',
    ]),
    sendVehicleCommandScope: makePermission([
        'SendVehicleCommand',
        'DescribeVehicleMetadata',
        'DescribeVehicleTelemetry',
    ]),
    sendEmergencyStopCommandScope: makePermission([
        'SendEmergencyStopCommand',
        'DescribeVehicleMetadata',
        'DescribeVehicleTelemetry',
    ]),
    updateMediaConfigScope: makePermission([
        'UpdateMediaConfig',
    ]),
};

export type KnownPermission = keyof typeof _knownPermissions;

const { useWithState, useDispatch, useState } = createGlobalState<Set<KnownPermission> | undefined>((_, a) => a, undefined);

export { useWithState as useWithScope };

export function useUpdateScope(projectId: Project.Id | undefined, environmentId: Environment.Id | undefined): void {
    const setScope = useDispatch();
    const authTokenRepository = useAuthTokenRepository();

    useEffect(() => {
        if (!authTokenRepository) {
            console.error('cannot obtain assertRepository');
            return;
        }

        if (!projectId || !environmentId) {
            return;
        }

        const knownPermissions = Object.values(_knownPermissions);
        knownPermissions.forEach(value => {
            value.resource.projectId = projectId;
            value.resource.environmentId = environmentId;
        });

        return cancellable(async isCancel => {
            const accessToken = await authTokenRepository.get();
            try {
                const validPermissions = await authTokenRepository.assertScope(accessToken, knownPermissions);
                if (isCancel()) return;

                const validKeys = (Object.keys(_knownPermissions) as KnownPermission[])
                    .filter(key => validPermissions.find(p => equalPermission(p, _knownPermissions[key])));

                setScope(new Set(validKeys));
            } catch (e) {
                console.error(`cannot obtain responseScope due to: ${JSON.stringify(e)}`);
            }
        });
    }, [authTokenRepository, environmentId, projectId, setScope]);
}

export function useScopeCheck(permission: KnownPermission) {
    const validScope = useState();
    return useMemo(() => validScope?.has(permission), [permission, validScope]);
}
